import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { orderAPI } from '../services/orderApi';
import { productAPI } from '../services/productApi';
import { clientAPI, invoiceAPI, templateAPI } from '../services/api';
import { supplierAPI } from '../services/supplierApi';
import { Order, Product, Client, Supplier, OrderType, OrderStatus, OrderItem, Payment, PaymentMethod } from '../types';
import { useCompany } from '../context/CompanyContext';
import { useI18n } from '../hooks/useI18n';
import Layout from '../components/Layout';
import { Plus, Eye, CheckCircle, X, ShoppingCart, Trash2, ArrowUpRight, ArrowDownLeft, DollarSign, AlertTriangle, FileText, Filter, Search } from 'lucide-react';
import LowStockModal from '../components/LowStockModal';
import { useAuth } from "../context/AuthContext";
import OrderItemModal from '../components/OrderItemModal';
import { Edit } from 'lucide-react';

const OrdersPage: React.FC = () => {
    const navigate = useNavigate();
    const { user } = useAuth();

    const [orders, setOrders] = useState<Order[]>([]);
    const [products, setProducts] = useState<Product[]>([]);
    const [clients, setClients] = useState<Client[]>([]);
    const [suppliers, setSuppliers] = useState<Supplier[]>([]);
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);

    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showViewModal, setShowViewModal] = useState(false);
    const [showPaymentModal, setShowPaymentModal] = useState(false);
    const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);

    // Pagination states
    const [page, setPage] = useState(0);
    const [hasMore, setHasMore] = useState(true);
    const [totalElements, setTotalElements] = useState(0);

    // Filter states
    const [showFilters, setShowFilters] = useState(false);
    const [selectedStatus, setSelectedStatus] = useState<OrderStatus | ''>('');
    const [selectedOrderType, setSelectedOrderType] = useState<OrderType | ''>('');
    const [searchOrderId, setSearchOrderId] = useState('');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [viewingPDF, setViewingPDF] = useState<string | null>(null);

    const { selectedCompany } = useCompany();
    const { t, isRTL } = useI18n();

    useEffect(() => {
        if (selectedCompany.selectedCompanyId) {
            loadInitialOrders();
            loadResources();
        } else {
            setOrders([]);
            setLoading(false);
        }
    }, [selectedCompany.selectedCompanyId]);

    // Infinite scroll handler
    useEffect(() => {
        const handleScroll = () => {
            if (loadingMore || !hasMore) return;

            const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
            const scrollHeight = document.documentElement.scrollHeight;
            const clientHeight = window.innerHeight;

            // Load more when scrolled to within 200px of bottom
            if (scrollTop + clientHeight >= scrollHeight - 200) {
                loadMoreOrders();
            }
        };

        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, [loadingMore, hasMore, page]);

    const loadInitialOrders = async () => {
        try {
            setLoading(true);
            setPage(0);
            setHasMore(true);
            const response = await orderAPI.searchOrders({
                companyId: selectedCompany.selectedCompanyId!,
                status: selectedStatus || undefined,
                orderType: selectedOrderType || undefined,
                orderId: searchOrderId || undefined,
                startDate: startDate || undefined,
                endDate: endDate || undefined,
                page: 0,
                size: 100
            });
            setOrders(response.content);
            setTotalElements(response.totalElements);
            setHasMore(!response.last);
            setError(null);
        } catch (err) {
            setError(t('stock.orders.failedToLoad'));
            console.error('Error loading orders:', err);
        } finally {
            setLoading(false);
        }
    };

    const loadMoreOrders = async () => {
        if (loadingMore || !hasMore) return;

        try {
            setLoadingMore(true);
            const nextPage = page + 1;

            const response = await orderAPI.searchOrders({
                companyId: selectedCompany.selectedCompanyId!,
                status: selectedStatus || undefined,
                orderType: selectedOrderType || undefined,
                orderId: searchOrderId || undefined,
                startDate: startDate || undefined,
                endDate: endDate || undefined,
                page: nextPage,
                size: 100
            });

            setOrders([...orders, ...response.content]);
            setPage(nextPage);
            setHasMore(!response.last);
        } catch (err) {
            console.error('Error loading more orders:', err);
        } finally {
            setLoadingMore(false);
        }
    };

    const loadResources = async () => {
        try {
            const [productsData, clientsData, suppliersData] = await Promise.all([
                productAPI.getProductsByCompany(selectedCompany.selectedCompanyId!),
                clientAPI.getClientsByCompany(selectedCompany.selectedCompanyId!),
                supplierAPI.getSuppliersByCompany(selectedCompany.selectedCompanyId!)
            ]);
            setProducts(productsData);
            setClients(clientsData);
            setSuppliers(suppliersData);
        } catch (err) {
            console.error('Error loading resources:', err);
        }
    };

    const handleCreateOrder = async (orderData: Partial<Order>) => {
        try {
            const created = await orderAPI.createOrder({
                ...orderData,
                companyId: selectedCompany.selectedCompanyId!,
                status: 'RESERVATION'
            });
            setOrders([created, ...orders]);
            setSuccessMessage(t('stock.orders.orderCreated'));
            setShowCreateModal(false);
        } catch (err) {
            setError(t('stock.orders.failedToCreate'));
            console.error('Error creating order:', err);
        }
    };

    const handleConfirmOrder = async (id: string) => {
        try {
            const confirmed = await orderAPI.confirmOrder(id);
            setOrders(orders.map(o => o.id === id ? confirmed : o));
            setSuccessMessage(t('stock.orders.orderConfirmed'));
            if (selectedOrder?.id === id) setSelectedOrder(confirmed);
        } catch (err: any) {
            const msg = err.response?.data?.message || t('stock.orders.failedToConfirm');
            setError(msg);
            console.error('Error confirming order:', err);
        }
    };

    const handleCancelOrder = async (id: string) => {
        if (!window.confirm(t('stock.orders.confirmCancel'))) return;
        try {
            const cancelled = await orderAPI.cancelOrder(id);
            setOrders(orders.map(o => o.id === id ? cancelled : o));
            setSuccessMessage(t('stock.orders.orderCancelled'));
            if (selectedOrder?.id === id) setSelectedOrder(cancelled);
        } catch (err: any) {
            const msg = err.response?.data?.message || t('stock.orders.failedToCancel');
            setError(msg);
            console.error('Error cancelling order:', err);
        }
    };

    const handleAddPayment = async (id: string, paymentData: Partial<Payment>) => {
        try {
            const updated = await orderAPI.addPayment(id, paymentData);
            setOrders(orders.map(o => o.id === id ? updated : o));
            setSuccessMessage(t('stock.orders.paymentAdded'));
            setShowPaymentModal(false);
            if (selectedOrder?.id === id) setSelectedOrder(updated);
        } catch (err: any) {
            const msg = err.response?.data?.message || t('stock.orders.failedToAddPayment');
            setError(msg);
            console.error('Error adding payment:', err);
        }
    };

    const applyFilters = async () => {
        try {
            setLoading(true);
            setPage(0);
            setHasMore(true);

            const response = await orderAPI.searchOrders({
                companyId: selectedCompany.selectedCompanyId!,
                status: selectedStatus || undefined,
                orderType: selectedOrderType || undefined,
                orderId: searchOrderId || undefined,
                startDate: startDate || undefined,
                endDate: endDate || undefined,
                page: 0,
                size: 100
            });

            setOrders(response.content);
            setTotalElements(response.totalElements);
            setHasMore(!response.last);
            setError(null);
        } catch (err) {
            setError(t('stock.orders.failedToLoad'));
            console.error('Error applying filters:', err);
        } finally {
            setLoading(false);
        }
    };

    const clearFilters = () => {
        setSelectedStatus('');
        setSelectedOrderType('');
        setSearchOrderId('');
        setStartDate('');
        setEndDate('');
        loadInitialOrders();
    };

    const hasActiveFilters = () => {
        return selectedStatus || selectedOrderType || searchOrderId || startDate || endDate;
    };

    const getInvoiceType = (orderType: OrderType) => {
        if (orderType === 'SALE') {
            return 'SELL_INVOICE'
        } else if (orderType === 'PURCHASE') {
            return 'PURCHASE_INVOICE'
        }
    }

    const handleViewPDF = async (invoiceId: string) => {
        try {
            setViewingPDF(invoiceId);

            // Get company information to find the template ID
            const company = selectedCompany.selectedCompany;
            if (!company || !company.templateId) {
                throw new Error('No template selected for this company');
            }

            // Generate PDF using template and open in new tab
            await invoiceAPI.getInvoiceById(invoiceId).then(async invoice => {
                await templateAPI.openPDFInNewTab(company.templateId, invoice);
            })


        } catch (err: any) {
            console.error('Error generating PDF:', err);
            setError('Failed to generate PDF. Please make sure a template is selected for this company.');
        } finally {
            setViewingPDF(null);
        }
    };

    const handleGenerateInvoice = (order: Order) => {
        let invoiceType = order.status === 'RESERVATION' ? 'QUOTE' : getInvoiceType(order.orderType);
        const initialData = {
            orderId: order.id,
            clientId: order.orderType === 'SALE' ? order.clientId : order.supplierId,
            invoiceType,
            order: {
                vatRate: order.vatRate,
                items: order.items.map(item => {
                    let product = products.find(p => p.id === item.productId)
                    return {
                        code: product?.code || '',
                        description: product?.name || '',
                        unitPrice: item.unitPrice,
                        quantity: item.quantity,
                        unity: product?.unity || ''
                    }
                })
            }
        };
        navigate('/invoices/create', { state: { initialData } });
    };

    if (!selectedCompany.selectedCompanyId) {
        return (
            <Layout>
                <div className="flex items-center justify-center min-h-64">
                    <div className="text-center">
                        <AlertTriangle className="w-16 h-16 text-yellow-500 mx-auto mb-4" />
                        <h2 className="text-xl font-semibold text-gray-900 mb-2">{t('clients.noCompanySelected')}</h2>
                        <p className="text-gray-600 mb-4">{t('clients.noCompanyMessage')}</p>
                    </div>
                </div>
            </Layout>
        );
    }

    return (
        <Layout>
            <div className={`flex justify-between items-center mb-6 ${isRTL ? 'rtl' : 'ltr'}`} dir={isRTL ? 'rtl' : 'ltr'}>
                <div>
                    <h1 className="text-3xl font-bold text-gray-900">{t('stock.orders.title')}</h1>
                    <p className="text-gray-600 mt-1">
                        {t('stock.orders.subtitle', { companyName: selectedCompany.selectedCompany?.name })}
                    </p>
                </div>
                <div className={`flex items-center gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
                    <button
                        onClick={() => setShowFilters(!showFilters)}
                        className={`flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors ${hasActiveFilters() ? 'bg-blue-50 border-blue-300' : ''}`}
                    >
                        <Filter size={20} />
                        {t('stock.orders.filters')}
                        {hasActiveFilters() && (
                            <span className="ml-1 bg-blue-600 text-white text-xs rounded-full px-2 py-0.5">
                                {[selectedStatus, selectedOrderType, searchOrderId, startDate, endDate].filter(Boolean).length}
                            </span>
                        )}
                    </button>
                    <button
                        onClick={() => setShowCreateModal(true)}
                        className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center gap-2"
                    >
                        <Plus size={20} />
                        {t('stock.orders.createNew')}
                    </button>
                </div>
            </div>

            {/* Filters Panel */}
            {showFilters && (
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                        {/* Search by Order ID */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                {t('stock.orders.orderId')}
                            </label>
                            <div className="relative">
                                <Search className={`absolute ${isRTL ? 'right-3' : 'left-3'} top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4`} />
                                <input
                                    type="text"
                                    placeholder={t('stock.orders.searchOrderIdPlaceholder')}
                                    value={searchOrderId}
                                    onChange={(e) => setSearchOrderId(e.target.value)}
                                    className={`w-full ${isRTL ? 'pr-10 pl-3' : 'pl-10 pr-3'} py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500`}
                                />
                            </div>
                        </div>

                        {/* Order Status */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                {t('stock.orders.status')}
                            </label>
                            <select
                                value={selectedStatus}
                                onChange={(e) => setSelectedStatus(e.target.value as OrderStatus | '')}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                            >
                                <option value="">{t('stock.orders.allStatuses')}</option>
                                <option value="RESERVATION">{t('stock.orders.reservation')}</option>
                                <option value="CONFIRMED">{t('stock.orders.confirmed')}</option>
                                <option value="CANCELLED">{t('stock.orders.cancelled')}</option>
                                <option value="COMPLETED">{t('stock.orders.completed')}</option>
                            </select>
                        </div>

                        {/* Order Type */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                {t('stock.orders.orderType')}
                            </label>
                            <select
                                value={selectedOrderType}
                                onChange={(e) => setSelectedOrderType(e.target.value as OrderType | '')}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                            >
                                <option value="">{t('stock.orders.allOrderTypes')}</option>
                                <option value="SALE">{t('stock.orders.sale')}</option>
                                <option value="PURCHASE">{t('stock.orders.purchase')}</option>
                            </select>
                        </div>

                        {/* Start Date */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                {t('stock.orders.startDate')}
                            </label>
                            <input
                                type="date"
                                value={startDate}
                                onChange={(e) => setStartDate(e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                            />
                        </div>

                        {/* End Date */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                {t('stock.orders.endDate')}
                            </label>
                            <input
                                type="date"
                                value={endDate}
                                onChange={(e) => setEndDate(e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                            />
                        </div>
                    </div>

                    {/* Filter Actions */}
                    <div className={`flex justify-end gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
                        <button
                            onClick={clearFilters}
                            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                        >
                            {t('stock.orders.clearFilters')}
                        </button>
                        <button
                            onClick={applyFilters}
                            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700"
                        >
                            {t('stock.orders.applyFilters')}
                        </button>
                    </div>
                </div>
            )}

            {error && (
                <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4 flex justify-between items-center">
                    <span>{error}</span>
                    <button onClick={() => setError(null)}><X size={18} /></button>
                </div>
            )}

            {successMessage && (
                <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4 flex justify-between items-center">
                    <div className="flex items-center gap-2">
                        <CheckCircle size={18} />
                        <span>{successMessage}</span>
                    </div>
                    <button onClick={() => setSuccessMessage(null)}><X size={18} /></button>
                </div>
            )}

            <div className="bg-white shadow-md rounded-lg overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className={`px-6 py-3 ${isRTL ? 'text-right' : 'text-left'} text-xs font-medium text-gray-500 uppercase tracking-wider`}>
                                    {t('stock.orders.orderType')}
                                </th>
                                <th className={`px-6 py-3 ${isRTL ? 'text-right' : 'text-left'} text-xs font-medium text-gray-500 uppercase tracking-wider`}>
                                    {t('invoices.date')}
                                </th>
                                <th className={`px-6 py-3 ${isRTL ? 'text-right' : 'text-left'} text-xs font-medium text-gray-500 uppercase tracking-wider`}>
                                    {t('stock.orders.status')}
                                </th>
                                <th className={`px-6 py-3 ${isRTL ? 'text-right' : 'text-left'} text-xs font-medium text-gray-500 uppercase tracking-wider`}>
                                    {t('stock.orders.totalAmount')}
                                </th>
                                <th className={`px-6 py-3 ${isRTL ? 'text-right' : 'text-left'} text-xs font-medium text-gray-500 uppercase tracking-wider`}>
                                    {t('stock.orders.actions')}
                                </th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {orders.map((order) => (
                                <tr key={order.id} className="hover:bg-gray-50">
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="flex items-center gap-2">
                                            {order.orderType === 'SALE' ? (
                                                <ArrowUpRight className="text-red-500" size={18} />
                                            ) : (
                                                <ArrowDownLeft className="text-green-500" size={18} />
                                            )}
                                            <span className="text-sm font-medium text-gray-900">
                                                {order.orderType === 'SALE' ? t('stock.orders.sale') : t('stock.orders.purchase')}
                                            </span>
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                        {new Date(order.creationDate).toLocaleDateString()}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <span className={`px-2 py-1 text-xs font-semibold rounded-full ${order.status === 'RESERVATION' ? 'bg-yellow-100 text-yellow-800' :
                                            order.status === 'CONFIRMED' ? 'bg-blue-100 text-blue-800' :
                                                order.status === 'COMPLETED' ? 'bg-green-100 text-green-800' :
                                                    'bg-red-100 text-red-800'
                                            }`}>
                                            {t(`stock.orders.${order.status.toLowerCase()}`)}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-bold">
                                        {order.totalAmount.toFixed(2)}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                        <div className="flex space-x-2">
                                            <button
                                                onClick={() => {
                                                    setSelectedOrder(order);
                                                    setShowViewModal(true);
                                                }}
                                                className="text-blue-600 hover:text-blue-900"
                                                title={t('common.view')}
                                            >
                                                <Eye size={18} />
                                            </button>
                                            {!(order.orderType === 'SALE' && order.status === 'RESERVATION') && (
                                                order.invoiceId ? (
                                                    <button
                                                        onClick={() => handleViewPDF(order.invoiceId || '')}
                                                        className="text-blue-600 hover:text-blue-900"
                                                        title={t('invoices.viewInvoice')}
                                                    >
                                                        {viewingPDF === order.invoiceId ? (
                                                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-purple-600"></div>
                                                        ) : (
                                                            <FileText size={18} />
                                                        )}
                                                    </button>
                                                ) : (
                                                    <button
                                                        onClick={() => handleGenerateInvoice(order)}
                                                        className="text-purple-600 hover:text-purple-900"
                                                        title={t('invoices.createInvoice')}
                                                    >
                                                        <FileText size={18} />
                                                    </button>
                                                )
                                            )}
                                            {order.status === 'RESERVATION' && (
                                                <button
                                                    onClick={() => handleConfirmOrder(order.id)}
                                                    className="text-green-600 hover:text-green-900"
                                                    title={t('stock.orders.confirmOrder')}
                                                >
                                                    <CheckCircle size={18} />
                                                </button>
                                            )}
                                            {order.status === 'CONFIRMED' && (
                                                <button
                                                    onClick={() => {
                                                        setSelectedOrder(order);
                                                        setShowPaymentModal(true);
                                                    }}
                                                    className="text-indigo-600 hover:text-indigo-900"
                                                    title={t('stock.orders.addPayment')}
                                                >
                                                    <DollarSign size={18} />
                                                </button>
                                            )}
                                            {(order.status === 'RESERVATION' || order.status === 'CONFIRMED') && (
                                                <button
                                                    onClick={() => handleCancelOrder(order.id)}
                                                    className="text-red-600 hover:text-red-900"
                                                    title={t('common.cancel')}
                                                >
                                                    <X size={18} />
                                                </button>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                {/* Loading more indicator */}
                {loadingMore && (
                    <div className="text-center py-4">
                        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                        <p className="text-gray-600 mt-2">{t('common.loading')}</p>
                    </div>
                )}

                {/* No more orders indicator */}
                {!loading && !loadingMore && !hasMore && orders.length > 0 && (
                    <div className="text-center py-4 text-gray-500">
                        {t('stock.orders.noMoreOrders')}
                    </div>
                )}

                {/* No orders found */}
                {orders.length === 0 && !loading && (
                    <div className="text-center py-8 text-gray-500">
                        {hasActiveFilters() ? t('stock.orders.noOrdersMatchFilters') : t('stock.orders.noOrdersFound')}
                    </div>
                )}
            </div>

            {showCreateModal && (
                <CreateOrderModal
                    onClose={() => setShowCreateModal(false)}
                    onSubmit={handleCreateOrder}
                    clients={clients}
                    suppliers={suppliers}
                    products={products}
                />
            )}

            {showViewModal && selectedOrder && (
                <OrderViewModal
                    order={selectedOrder}
                    products={products}
                    clients={clients}
                    suppliers={suppliers}
                    onClose={() => setShowViewModal(false)}
                    onConfirm={() => handleConfirmOrder(selectedOrder.id)}
                    onCancel={() => handleCancelOrder(selectedOrder.id)}
                    onAddPayment={() => {
                        setShowViewModal(false);
                        setShowPaymentModal(true);
                    }}
                />
            )}

            {showPaymentModal && selectedOrder && (
                <PaymentModal
                    order={selectedOrder}
                    onClose={() => setShowPaymentModal(false)}
                    onSubmit={(data) => handleAddPayment(selectedOrder.id, data)}
                />
            )}
        </Layout>
    );
};

// --- Modals ---

interface CreateOrderModalProps {
    onClose: () => void;
    onSubmit: (data: Partial<Order>) => void;
    clients: Client[];
    suppliers: Supplier[];
    products: Product[];
}

const CreateOrderModal: React.FC<CreateOrderModalProps> = ({ onClose, onSubmit, clients, suppliers, products }) => {
    const { t, isRTL } = useI18n();
    const { selectedCompany } = useCompany();
    const [orderType, setOrderType] = useState<OrderType>('SALE');
    const [clientId, setClientId] = useState('');
    const [supplierId, setSupplierId] = useState('');
    const [vatRate, setVatRate] = useState(selectedCompany.selectedCompany?.vatRate || 0);
    const [items, setItems] = useState<Partial<OrderItem>[]>([]);
    const [showLowStockModal, setShowLowStockModal] = useState(false);
    const [lowStockItems, setLowStockItems] = useState<{ product: Product; requested: number }[]>([]);


    const [showItemModal, setShowItemModal] = useState(false);
    const [editingItem, setEditingItem] = useState<OrderItem | undefined>(undefined);
    const [editingIndex, setEditingIndex] = useState<number | null>(null);

    const handleAddItem = () => {
        setEditingItem(undefined);
        setEditingIndex(null);
        setShowItemModal(true);
    };

    const handleEditItem = (index: number) => {
        setEditingItem(items[index] as OrderItem);
        setEditingIndex(index);
        setShowItemModal(true);
    };

    const handleDeleteItem = (index: number) => {
        setItems(items.filter((_, i) => i !== index));
    };

    const handleSaveItem = (item: OrderItem) => {
        if (editingIndex !== null) {
            const newItems = [...items];
            newItems[editingIndex] = item;
            setItems(newItems);
        } else {
            setItems([...items, item]);
        }
        setShowItemModal(false);
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (items.length === 0) return;

        if (orderType === 'SALE') {
            const lowStock = items.map(item => {
                const product = products.find(p => p.id === item.productId);
                if (product && (product.quantityOnHand - product.reservedQuantity) < (item.quantity || 0)) {
                    return { product, requested: item.quantity || 0 };
                }
                return null;
            }).filter(item => item !== null) as { product: Product; requested: number }[];

            if (lowStock.length > 0) {
                setLowStockItems(lowStock);
                setShowLowStockModal(true);
                return;
            }
        }

        onSubmit({
            orderType,
            clientId: orderType === 'SALE' ? (clientId || undefined) : undefined,
            supplierId: orderType === 'PURCHASE' ? (supplierId || undefined) : undefined,
            vatRate,
            items: items as OrderItem[]
        });
    };

    const handleConfirmLowStock = () => {
        setShowLowStockModal(false);
        onSubmit({
            orderType,
            clientId: orderType === 'SALE' ? (clientId || undefined) : undefined,
            supplierId: orderType === 'PURCHASE' ? (supplierId || undefined) : undefined,
            items: items as OrderItem[]
        });
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className={`bg-white rounded-lg p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto ${isRTL ? 'rtl' : 'ltr'}`} dir={isRTL ? 'rtl' : 'ltr'}>
                <h2 className="text-2xl font-bold mb-4 flex items-center gap-2">
                    <ShoppingCart className="text-blue-600" />
                    {t('stock.orders.createNew')}
                </h2>
                <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">{t('stock.orders.orderType')}</label>
                            <select
                                value={orderType}
                                onChange={(e) => setOrderType(e.target.value as OrderType)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                            >
                                <option value="SALE">{t('stock.orders.sale')}</option>
                                <option value="PURCHASE">{t('stock.orders.purchase')}</option>
                            </select>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                {orderType === 'SALE' ? t('invoices.client') : t('suppliers.title')}
                            </label>
                            {orderType === 'SALE' ? (
                                <select
                                    value={clientId}
                                    onChange={(e) => setClientId(e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                >
                                    <option value="">{t('common.select')}</option>
                                    {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                                </select>
                            ) : (
                                <select
                                    value={supplierId}
                                    onChange={(e) => setSupplierId(e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                >
                                    <option value="">{t('common.select')}</option>
                                    {suppliers.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                                </select>
                            )}
                        </div>
                    </div>

                    <div className="bg-blue-50 p-3 rounded-md flex justify-between items-center">
                        <span className="text-sm text-blue-800 font-medium">{t('invoices.vatRate')}</span>
                        <div className="flex items-center gap-2">
                            <input
                                type="number"
                                min="0"
                                step="0.01"
                                value={vatRate}
                                onChange={(e) => setVatRate(parseFloat(e.target.value))}
                                className="w-20 px-2 py-1 border border-blue-300 rounded-md text-right text-sm font-bold text-blue-800 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                            <span className="text-sm text-blue-800 font-bold">%</span>
                        </div>
                    </div>

                    <div className="space-y-4">
                        <div className="flex justify-between items-center">
                            <h3 className="text-lg font-semibold">{t('stock.orders.items')}</h3>
                            <button
                                type="button"
                                onClick={handleAddItem}
                                className="text-blue-600 hover:text-blue-800 flex items-center gap-1"
                            >
                                <Plus size={18} /> {t('invoices.addProduct')}
                            </button>
                        </div>

                        {items.length > 0 ? (
                            <div className="border rounded-md overflow-hidden">
                                <table className="min-w-full divide-y divide-gray-200">
                                    <thead className="bg-gray-50">
                                        <tr>
                                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">{t('stock.products.name')}</th>
                                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">{t('invoices.quantity')}</th>
                                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">{t('invoices.price')}</th>
                                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">{t('invoices.total')}</th>
                                            <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">{t('common.actions')}</th>
                                        </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-gray-200">
                                        {items.map((item, index) => {
                                            const product = products.find(p => p.id === item.productId);
                                            return (
                                                <tr key={index}>
                                                    <td className="px-4 py-2 text-sm text-gray-900">
                                                        {product?.name || item.description || '-'}
                                                        {product && <div className="text-xs text-gray-500">{product.code}</div>}
                                                    </td>
                                                    <td className="px-4 py-2 text-sm text-gray-900">{item.quantity} {item.unity}</td>
                                                    <td className="px-4 py-2 text-sm text-gray-900">{item.unitPrice?.toFixed(2)}</td>
                                                    <td className="px-4 py-2 text-sm text-gray-900">{((item.quantity || 0) * (item.unitPrice || 0)).toFixed(2)}</td>
                                                    <td className="px-4 py-2 text-right text-sm font-medium">
                                                        <button
                                                            type="button"
                                                            onClick={() => handleEditItem(index)}
                                                            className="text-blue-600 hover:text-blue-900 mr-3"
                                                        >
                                                            <Edit size={16} />
                                                        </button>
                                                        <button
                                                            type="button"
                                                            onClick={() => handleDeleteItem(index)}
                                                            className="text-red-600 hover:text-red-900"
                                                        >
                                                            <Trash2 size={16} />
                                                        </button>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        ) : (
                            <div className="text-center py-8 bg-gray-50 rounded-md border border-dashed border-gray-300 text-gray-500">
                                {t('invoices.noItems')}
                            </div>
                        )}
                    </div>

                    <div className={`flex justify-end gap-3 pt-4 ${isRTL ? 'flex-row-reverse' : ''}`}>
                        <button type="button" onClick={onClose} className="px-4 py-2 border border-gray-300 rounded-md">
                            {t('common.cancel')}
                        </button>
                        <button
                            type="submit"
                            disabled={items.length === 0}
                            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400"
                        >
                            {t('common.create')}
                        </button>
                    </div>
                </form>

                <LowStockModal
                    isOpen={showLowStockModal}
                    onClose={() => setShowLowStockModal(false)}
                    onConfirm={handleConfirmLowStock}
                    lowStockItems={lowStockItems}
                />

                <OrderItemModal
                    isOpen={showItemModal}
                    onClose={() => setShowItemModal(false)}
                    onSave={handleSaveItem}
                    item={editingItem}
                    mode={editingItem ? 'edit' : 'create'}
                    currency={'MAD'} // Default to MAD as property doesn't exist on Company model
                    products={products}
                />
            </div>
        </div>
    );
};

interface OrderViewModalProps {
    order: Order;
    products: Product[];
    clients: Client[];
    suppliers: Supplier[];
    onClose: () => void;
    onConfirm: () => void;
    onCancel: () => void;
    onAddPayment: () => void;
}

const OrderViewModal: React.FC<OrderViewModalProps> = ({ order, products, clients, suppliers, onClose, onConfirm, onCancel, onAddPayment }) => {
    const { t, isRTL } = useI18n();
    const totalPaid = order.paymentHistory.reduce((sum, p) => sum + p.amount, 0);

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className={`bg-white rounded-lg p-6 w-full max-w-3xl max-h-[90vh] overflow-y-auto ${isRTL ? 'rtl' : 'ltr'}`} dir={isRTL ? 'rtl' : 'ltr'}>
                <div className="flex justify-between items-start mb-6">
                    <h2 className="text-2xl font-bold">{t('stock.orders.orderDetails')}
                        <span className="text-sm text-gray-500"> | {order.id}</span></h2>
                    <button onClick={onClose}><X size={24} /></button>
                </div>

                <div className="grid grid-cols-2 gap-6 mb-8">
                    <div className="space-y-2">
                        <p><span className="font-semibold">{t('stock.orders.orderType')}:</span> {t(`stock.orders.${order.orderType.toLowerCase()}`)}</p>
                        <p>
                            <span className="font-semibold">
                                {order.orderType === 'SALE' ? t('invoices.client') : t('suppliers.title')}:
                            </span>{' '}
                            {order.orderType === 'SALE'
                                ? (clients.find(c => c.id === order.clientId)?.name || order.clientId || '-')
                                : (suppliers.find(s => s.id === order.supplierId)?.name || order.supplierId || '-')
                            }
                        </p>
                        <p><span className="font-semibold">{t('stock.orders.status')}:</span> {t(`stock.orders.${order.status.toLowerCase()}`)}</p>
                        <p><span className="font-semibold">{t('invoices.date')}:</span> {new Date(order.creationDate).toLocaleString()}</p>
                        <p><span className="font-semibold">{t('invoices.vatRate')}:</span> {order.vatRate}%</p>
                    </div>
                    <div className="space-y-2 text-right">
                        <p className="text-sm text-gray-500">{t('invoices.totalHT')}: {order.totalHT.toFixed(2)}</p>
                        <p className="text-sm text-gray-500">{t('invoices.vatAmount')}: {(order.totalHT * (order.vatRate / 100)).toFixed(2)}</p>
                        <p className="text-xl font-bold">{t('invoices.totalTTC')}: {order.totalAmount.toFixed(2)}</p>
                        <p className="text-green-600 font-semibold">{t('stock.orders.totalPaid')}: {totalPaid.toFixed(2)}</p>
                        <p className="text-red-600 font-semibold">{t('stock.orders.remainingBalance')}: {(order.totalAmount - totalPaid).toFixed(2)}</p>
                    </div>
                </div>

                <h3 className="text-lg font-semibold mb-3 border-b pb-2">{t('stock.orders.items')}</h3>
                <table className="min-w-full mb-8">
                    <thead>
                        <tr className="text-left text-sm text-gray-500">
                            <th className="pb-2">{t('stock.products.name')}</th>
                            <th className="pb-2">{t('invoices.quantity')}</th>
                            <th className="pb-2">{t('invoices.price')}</th>
                            <th className="pb-2 text-right">{t('invoices.total')}</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y">
                        {order.items.map((item, i) => (
                            <tr key={i} className="text-sm">
                                <td className="py-2">
                                    {products.find(p => p.id === item.productId)?.name || item.code || item.productId}
                                </td>
                                <td className="py-2">{item.quantity}</td>
                                <td className="py-2">{item.unitPrice.toFixed(2)}</td>
                                <td className="py-2 text-right">{(item.quantity * item.unitPrice).toFixed(2)}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>

                {order.paymentHistory.length > 0 && (
                    <>
                        <h3 className="text-lg font-semibold mb-3 border-b pb-2">{t('stock.orders.paymentHistory')}</h3>
                        <table className="min-w-full mb-8">
                            <thead>
                                <tr className="text-left text-sm text-gray-500">
                                    <th className="pb-2">{t('invoices.date')}</th>
                                    <th className="pb-2">{t('invoices.amount')}</th>
                                    <th className="pb-2">{t('invoices.paymentMethod')}</th>
                                    <th className="pb-2">{t('common.description')}</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y">
                                {order.paymentHistory.map((p, i) => (
                                    <tr key={i} className="text-sm">
                                        <td className="py-2">{new Date(p.date).toLocaleDateString()}</td>
                                        <td className="py-2 font-semibold">{p.amount.toFixed(2)}</td>
                                        <td className="py-2">{t(`common.payments.${p.method}`)}</td>
                                        <td className="py-2 text-gray-500">{p.description}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </>
                )}

                <div className={`flex justify-end gap-3 mt-6 ${isRTL ? 'flex-row-reverse' : ''}`}>
                    {order.status === 'RESERVATION' && (
                        <button
                            onClick={onConfirm}
                            className="px-6 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 font-bold"
                        >
                            {t('stock.orders.confirmOrder')}
                        </button>
                    )}
                    {order.status === 'CONFIRMED' && (
                        <button
                            onClick={onAddPayment}
                            className="px-6 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 font-bold"
                        >
                            {t('stock.orders.addPayment')}
                        </button>
                    )}
                    {(order.status === 'RESERVATION' || order.status === 'CONFIRMED') && (
                        <button
                            onClick={onCancel}
                            className="px-6 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 font-bold"
                        >
                            {t('common.cancel')}
                        </button>
                    )}
                    <button onClick={onClose} className="px-6 py-2 border border-gray-300 rounded-md">
                        {t('common.close')}
                    </button>
                </div>
            </div>
        </div>
    );
};

interface PaymentModalProps {
    order: Order;
    onClose: () => void;
    onSubmit: (data: Partial<Payment>) => void;
}

const PaymentModal: React.FC<PaymentModalProps> = ({ order, onClose, onSubmit }) => {
    const { t, isRTL } = useI18n();
    const totalPaid = order.paymentHistory.reduce((sum, p) => sum + p.amount, 0);
    const remaining = order.totalAmount - totalPaid;

    const [amount, setAmount] = useState(remaining);
    const [method, setMethod] = useState('CASH');
    const [description, setDescription] = useState('');

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        onSubmit({ amount, method: method as any, description, date: new Date().toISOString() });
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className={`bg-white rounded-lg p-6 w-full max-w-md ${isRTL ? 'rtl' : 'ltr'}`} dir={isRTL ? 'rtl' : 'ltr'}>
                <h2 className="text-2xl font-bold mb-4">{t('stock.orders.addPayment')}</h2>
                <div className="mb-4 p-3 bg-blue-50 rounded-md">
                    <p className="text-sm text-blue-800">{t('stock.orders.remainingBalance')}: <span className="font-bold">{remaining.toFixed(2)}</span></p>
                </div>
                <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">{t('invoices.amount')} *</label>
                        <input
                            type="number"
                            step="0.01"
                            required
                            max={remaining}
                            value={amount}
                            onChange={(e) => setAmount(parseFloat(e.target.value))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">{t('invoices.paymentMethod')} *</label>
                        <select
                            value={method}
                            onChange={(e) => setMethod(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md"
                        >
                            {Object.values(PaymentMethod).map((method) => (
                                <option key={method} value={method}>
                                    {t(`common.payments.${method}`)}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">{t('common.description')}</label>
                        <textarea
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md"
                            rows={2}
                        />
                    </div>
                    <div className={`flex justify-end gap-3 pt-4 ${isRTL ? 'flex-row-reverse' : ''}`}>
                        <button type="button" onClick={onClose} className="px-4 py-2 border border-gray-300 rounded-md">
                            {t('common.cancel')}
                        </button>
                        <button type="submit" className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md">
                            {t('common.submit')}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default OrdersPage;
